// MainComponent.js
import { Link } from 'react-router-dom';
import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FreePlansModal from './FreePlansModal';
import FormsBottomNavbar from '../Post Module/FormsBottomNavbar';
import { BASE_URL } from "../../Api/ApiUrls";
import { AuthContext } from "../../AuthContext/AuthContext";
import Dashboard_verticalside from './Dashboard_verticalside';

// Import all components
import TopHeader from './TopHeader';
import SearchAppBar from './SearchAppBar';
import LookingForCards from './LookingForCards';
import InfoCards from './InfoCards';
import ConstructionCards from './ConstructionCards';
import HomeServicesCards from './HomeServicesCards';

function App() {
  const { userId, logout } = useContext(AuthContext);
  const [userData, setUserData] = useState(null);
  const [query, setQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [hasExistingEnquiry, setHasExistingEnquiry] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);

  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (location.state?.showFreePlanPopup) {
      setOpen(true);

      // ✅ Clear state so it won't trigger again
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const cardStyle = {
    backgroundColor: "#d8ccba",
    borderTopLeftRadius: "24px",
    borderBottomRightRadius: "24px",
    overflow: "hidden",
    // width: "100%",
    // height: 120,
    boxShadow: "0 13px 30px rgba(0, 0, 0, 0.4), 0 6px 10px rgba(0, 0, 0, 0.35)",
    cursor: "pointer",
    transform: "perspective(1000px)",
    transition: "transform 0.4s ease, box-shadow 0.4s ease",
    "&:hover": {
      transform: "perspective(1000px) scale(1.03) rotateX(2deg)",
      boxShadow: "0 28px 40px rgba(0, 0, 0, 0.5), 0 10px 20px rgba(0, 0, 0, 0.4)",
    },
  };

  const handleModalClose = (success) => {
    setShowModal(false);
    if (success) {
      // Handle successful subscription
      console.log('User subscribed to free plan successfully');
      navigate('/user-subscription'); // Redirect to subscriptions page
      // You might want to update user state or show a notification
    }
  };

  const [notificationData, setNotificationData] = useState(null);

  // Function to mark notification as read (keep this as is)
  const markNotificationAsRead = async (notificationId) => {
    console.log('markNotificationAsRead called with ID:', notificationId);
    
    try {
      const response = await fetch(`${BASE_URL}/notifications/read/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notification_id: notificationId,
        }),
      });

      console.log('API Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('API Success:', result);
        
        // Update local state to reflect the read status
        setNotificationData(prevData => {
          if (!prevData) return prevData;
          
          console.log('Updating state for notification ID:', notificationId);
          const updatedData = { ...prevData };
          
          // Update in all notification types
          ['liked', 'general', 'chat'].forEach(type => {
            const key = `notifications_${type}`;
            if (updatedData[key]) {
              updatedData[key] = updatedData[key].map(notification => 
                notification.notification_id === notificationId 
                  ? { ...notification, is_read: true }
                  : notification
              );
            }
          });
          
          // Update unread counts
          if (updatedData.notifications_liked?.some(n => n.notification_id === notificationId && !n.is_read)) {
            updatedData.unread_count_liked = Math.max(0, updatedData.unread_count_liked - 1);
            console.log('Updated liked count:', updatedData.unread_count_liked);
          }
          if (updatedData.notifications_newprop?.some(n => n.notification_id === notificationId && !n.is_read)) {
            updatedData.unread_count_newprop = Math.max(0, updatedData.unread_count_newprop - 1);
            console.log('Updated newprop count:', updatedData.unread_count_newprop);
          }
          if (updatedData.notifications_chat?.some(n => n.notification_id === notificationId && !n.is_read)) {
            updatedData.unread_count_chat = Math.max(0, updatedData.unread_count_chat - 1);
            console.log('Updated chat count:', updatedData.unread_count_chat);
          }
          
          return updatedData;
        });
      } else {
        console.error('API Error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Updated function to handle notification click and navigation
  const handleNotificationClick = async (notification) => {
    const {
      notification_id,
      notification_type,
      property_id,
      property_type,
      user_id,
    } = notification;

    console.log("Notification clicked:", notification);

    try {
      // 1. Mark as read
      await markNotificationAsRead(notification_id);

      /* ---------------- Liked ---------------- */
      if (notification_type === "Liked") {
        if (property_id) {
          navigate(`/wishlist-users/${property_id}`);
        }
        return;
      }

      /* ---------------- Chat ---------------- */
      if (notification_type === "Chat") {
        if (property_id && user_id) {
          navigate(`/chat/${property_id}/${user_id}`);
        }
        return;
      }

      /* ---------------- New Property ---------------- */
      if (notification_type === "general") {
        if (!property_id || !property_type) {
          console.warn("Missing property data");
          return;
        }

        if (property_type === "sell") {
          navigate(`/buy-map/${property_id}`);
        } 
        else if (property_type === "lease") {
          navigate(`/lease-description/${property_id}`);
        } 
        else if (property_type === "rent") {
          navigate(`/rent-description/${property_id}`);
        } 
        else if (property_type === "best-deal") {
          navigate(`/hot-property-description/${property_id}`);
        } 
        else {
          console.warn("Unhandled property_type:", property_type);
        }

        return;
      }

      console.warn("Unknown notification_type:", notification_type);

    } catch (error) {
      console.error("Notification click error:", error);
    }
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        console.log('Fetching notifications...');
        const response = await fetch(`${BASE_URL}/notifications/unread/${userId}/`);
        const data = await response.json();
        console.log('Fetched notifications:', data);
        setNotificationData(data);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(interval);
  }, [userId]); // Added userId dependency

  useEffect(() => {
    const checkShouldShowModal = async () => {
      if (!userId) {
        setShowModal(false);
        setIsCheckingSubscription(false);
        return;
      }

      try {
        const response = await axios.get(`${BASE_URL}/subusers/`);
        const userSubscriptions = response.data.filter(sub => sub.user_id === parseInt(userId));
        
        // Only show modal if user has no subscriptions
        setShowModal(userSubscriptions.length === 0);
      } catch (error) {
        console.error('Error checking subscriptions:', error);
        // Default to showing modal if check fails
        setShowModal(true);
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    checkShouldShowModal();
  }, [userId]); // Re-run when userId changes

  // useEffect(() => {
  //   const checkExistingEnquiry = async () => {
  //     try {
  //       // Fetch all enquiries for this user
  //       const response = await axios.get(`${BASE_URL}/Enquiry/`);
  //       const userEnquiries = response.data.filter(
  //         enquiry => enquiry.user_id == userId
  //       );

  //       if (userEnquiries.length === 0) {
  //         // Only show form if user has no existing enquiries
  //         const timer = setTimeout(() => {
  //           setShowForm(true);
  //         }, 3000);
  //         return () => clearTimeout(timer);
  //       } else {
  //         setHasExistingEnquiry(true);
  //       }
  //     } catch (error) {
  //       console.error("Error checking existing enquiries:", error);
  //       // Fallback - show form anyway
  //       const timer = setTimeout(() => {
  //         setShowForm(true);
  //       }, 3000);
  //       return () => clearTimeout(timer);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   checkExistingEnquiry();
  // }, [userId]);

  const handleFormSubmit = async (formData) => {
    try {
      const payload = {
        user_id: userId,
        area: formData.area?.trim(),
        property_type: formData.propertyType,
        min_budget: formData.min_budget,  // already a number or null from the range selection
        max_budget: formData.max_budget,
        lat: formData.latitude,
        long: formData.longitude
      };

      const response = await axios.post(`${BASE_URL}/Enquiry/`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Mark that user now has an enquiry
      setHasExistingEnquiry(true);
      setShowForm(false);
    } catch (error) {
      console.error("API Error:", error);
      if (error.response) {
        alert(`API Error: ${JSON.stringify(error.response.data)}`);
      } else {
        alert("Network error: " + error.message);
      }
    }
  };

  useEffect(() => {
    if (!userId) return;

    // Fetch user data
    fetch(`${BASE_URL}/users/${userId}/`)
      .then((response) => response.json())
      .then((data) => {
        setUserData(data);
      })
      .catch((error) => console.error("Error fetching user data:", error));
  }, [userId]);

 const handleKeyPress = (e) => {
  if (e.key === 'Enter') {
    const lowerQuery = query.toLowerCase().trim();

    if (lowerQuery.includes('rent')) {
      navigate('/rent-propertymap');
    } else if (lowerQuery.includes('lease')) {
      navigate('/lease-map');
    } else if (lowerQuery.includes('buy')) {
      navigate('/buy-map');
    } else if (lowerQuery.includes('service')) {
      navigate('/home-service');
    } else if (lowerQuery.includes('jvjd')) {
      navigate('/jvjd');
    } else if (
      lowerQuery.includes('bank auction') ||
      lowerQuery.includes('bank auctions') ||
      lowerQuery.includes('auction')
    ) {
      navigate('/postbank-card');
    } else {
      alert('No matching service found. Redirecting to dashboard.');
      navigate('/dashboard');
    }
  }
};

  const handleSearch = () => {
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('rent')) {
      navigate('/rent-propertymap');
    } else if (lowerQuery.includes('lease')) {
      navigate('/lease-map');
    } else if (lowerQuery.includes('buy')) {
      navigate('/buy-map');
    } else if (lowerQuery.includes('service')) {
      navigate('/home-service');
    } else {
      alert('No matching service found. Redirecting to dashboard.');
      navigate('/dashboard');
    }
  };
  // if (loading) {
  //   return <div>Loading...</div>;
  // }

  // if (isCheckingSubscription) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  return (
    <>
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={() => setOpen(false)} 
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ fontWeight: "bold" }}>
          🎉 You have been successfully activated with Free Trial Plan!
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: ' #f8f9fa' }}>
        <TopHeader />

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', marginBottom: "70px" }}>
          <Dashboard_verticalside />

          <Box flex={1} p={1} sx={{ overflow: 'auto' }}>
            <SearchAppBar 
              query={query}
              setQuery={setQuery}
              handleKeyPress={handleKeyPress}
              notificationData={notificationData}
              handleNotificationClick={handleNotificationClick}
            />

            {/* Add spacing to avoid content going under the AppBar */}
            <Box
              mb={2}
              mt={7}
              sx={{
                backgroundColor: '#e7dbc9',
                width: '90%',
                height: '340px',
                mx: 'auto',
                borderRadius: '12px',
                p: 2
              }}
            >
              <Typography variant="h6" fontWeight="bold" align="center" mb={1}>
                Property Search
              </Typography>
              <LookingForCards navigate={navigate} />
            </Box>
            
            <Box>
              <InfoCards navigate={navigate} />
            </Box>

            {/* Construction cards */}
            <Box mb={2}>
              <Typography variant="h6" fontWeight="bold" align="center" mb={1}>
                Constructions
              </Typography>
              <ConstructionCards navigate={navigate} />
            </Box>

            {/* Home services */}
            <Box mb={2}>
              <Typography variant="h6" fontWeight="bold" align="center" mb={1}>
                Home Services
              </Typography>
              <HomeServicesCards navigate={navigate} />
            </Box>
          </Box>
        </Box>

        <FormsBottomNavbar />
      </Box>
    </>
  );
}

export default App;